import type {
  AdminInvoiceDetailDTO,
  InvoiceApplicationPreviewDTO,
  InvoiceApplicationConfirmInput,
  PaymentRecordInput,
} from "@ai-crm/shared";
import { useRef, useState } from "react";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { adminApi, ApiError } from "../../lib/api";
import { formatMoscowDateTime, formatPaymentRub } from "../../lib/format";
import {
  paymentButton,
  paymentPrimary,
  paymentError,
  usePaymentScope,
  usePaymentWrite,
  usePreviewExpiry,
} from "../../lib/payment-ui";
import {
  initialPaymentRecord,
  parsePaymentRecord,
  PaymentRecordForm,
  PaymentAllocationFields,
  readAllocations,
} from "./payment-record-form";

export interface InvoiceActionProps {
  accountId: string;
  invoice: AdminInvoiceDetailDTO;
  onChanged: (replacementId?: string) => Promise<void>;
}
export function PaymentApplicationPanel({
  accountId,
  invoice,
  onChanged,
}: InvoiceActionProps) {
  const scope = usePaymentScope(accountId);
  const write = usePaymentWrite(accountId);
  const [draft, setDraft] = useState(() =>
    initialPaymentRecord(invoice.payer, invoice.totalRub),
  );
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [includeNew, setIncludeNew] = useState(false);
  const [newAllocation, setNewAllocation] = useState(invoice.totalRub);
  const [preview, setPreview] = useState<InvoiceApplicationPreviewDTO | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const previewPending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const expired = usePreviewExpiry(preview?.expiresAt);
  const upgrade = invoice.action.kind === "UPGRADE";
  const locked = write.locked || loading;
  const mayApply = upgrade
    ? invoice.allowedCommands.previewUpgrade
    : invoice.allowedCommands.previewApplication;
  function changed() {
    if (!locked) {
      setPreview(null);
      setError(null);
      setMessage(null);
      write.clearError();
    }
  }
  async function conflict(caught: unknown) {
    if (caught instanceof ApiError && caught.status === 409) {
      setPreview(null);
      setConfirming(false);
      await onChanged();
    }
  }
  async function record(mode: PaymentRecordInput["mode"]) {
    if (!scope.token || locked) return;
    let payment;
    try {
      payment = parsePaymentRecord(draft);
      if (mode === "CONFIRM" && !payment.payer.inn)
        throw new Error("Без ИНН запишите перевод для проверки.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Проверьте форму");
      return;
    }
    const input: PaymentRecordInput = {
      requestId: crypto.randomUUID(),
      expectedVersion: invoice.version,
      payment,
      mode,
    };
    const token = scope.token;
    await write.run(
      input,
      (body) => adminApi.recordPayment(token, accountId, invoice.id, body),
      async () => {
        setMessage(
          mode === "CONFIRM"
            ? "Поступление подтверждено. Покупка ещё не применена."
            : "Поступление записано для проверки. Доступ не изменён.",
        );
        setPreview(null);
        await onChanged();
      },
      conflict,
    );
  }
  async function requestPreview() {
    if (!scope.token || locked || previewPending.current || !mayApply) return;
    const guard = scope.capture();
    previewPending.current = true;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const selected = readAllocations(allocations);
      const result = upgrade
        ? await adminApi.previewInvoiceUpgrade(
            scope.token,
            accountId,
            invoice.id,
            {
              expectedVersion: invoice.version,
              allocations: selected,
              newPayment: includeNew ? parsePaymentRecord(draft) : null,
              newPaymentAllocationRub: includeNew
                ? (readAllocations({ new: newAllocation })[0]?.amountRub ??
                  null)
                : null,
            },
          )
        : await adminApi.previewInvoiceApplication(
            scope.token,
            accountId,
            invoice.id,
            { expectedVersion: invoice.version, allocations: selected },
          );
      if (guard.isCurrent()) setPreview(result);
    } catch (caught) {
      if (guard.isCurrent()) {
        setError(
          caught instanceof ApiError
            ? paymentError(caught)
            : caught instanceof Error
              ? caught.message
              : "Проверьте параметры",
        );
        await conflict(caught);
      }
    } finally {
      if (guard.isCurrent()) {
        previewPending.current = false;
        setLoading(false);
      }
    }
  }
  async function confirm() {
    if (!preview || !scope.token || locked || expired) return;
    const input: InvoiceApplicationConfirmInput = {
      requestId: crypto.randomUUID(),
      expectedVersion: preview.invoiceVersion,
      previewToken: preview.previewToken,
    };
    const token = scope.token;
    setConfirming(false);
    await write.run(
      input,
      (body) =>
        upgrade
          ? adminApi.confirmInvoiceUpgrade(token, accountId, invoice.id, body)
          : adminApi.confirmInvoiceApplication(
              token,
              accountId,
              invoice.id,
              body,
            ),
      async () => {
        setPreview(null);
        setMessage(
          upgrade
            ? "Оплата подтверждена, тариф повышен."
            : "Покупка применена.",
        );
        await onChanged();
      },
      conflict,
    );
  }
  return (
    <section
      className="space-y-4 rounded-md border p-4"
      aria-label="Поступление и применение"
      aria-busy={locked}
    >
      <h3 className="font-semibold">
        {upgrade
          ? "Подтверждение оплаты и повышение тарифа"
          : "Поступление и отдельное применение"}
      </h3>
      <p className="break-words text-sm text-muted-foreground">
        Банк получателя: {invoice.recipient.bankName}, счёт{" "}
        {invoice.recipient.bankAccount}.
      </p>
      {(invoice.allowedCommands.recordPayment || (upgrade && mayApply)) && (
        <>
          <PaymentRecordForm
            value={draft}
            disabled={locked}
            onChange={(next) => {
              setDraft(next);
              changed();
            }}
          />
          {invoice.allowedCommands.recordPayment && (
            <div className="flex flex-wrap gap-2">
              {!upgrade && (
                <button
                  className={paymentPrimary}
                  disabled={locked || !draft.payerInn.trim()}
                  onClick={() => void record("CONFIRM")}
                >
                  Подтвердить поступление
                </button>
              )}
              <button
                className={paymentButton}
                disabled={locked}
                onClick={() => void record("RECORD_FOR_REVIEW")}
              >
                Записать для проверки
              </button>
            </div>
          )}
        </>
      )}
      {mayApply && (
        <>
          <PaymentAllocationFields
            payments={invoice.payments}
            values={allocations}
            disabled={locked}
            onChange={(next) => {
              setAllocations(next);
              changed();
            }}
          />
          {upgrade && (
            <div className="space-y-2">
              <label className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeNew}
                  disabled={locked}
                  onChange={(e) => {
                    setIncludeNew(e.target.checked);
                    changed();
                  }}
                />
                Включить новый перевод из формы выше
              </label>
              {includeNew && (
                <label className="block space-y-1 text-sm">
                  <span>Зачесть из нового перевода, ₽</span>
                  <input
                    className="control"
                    inputMode="decimal"
                    value={newAllocation}
                    disabled={locked}
                    onChange={(e) => {
                      setNewAllocation(e.target.value);
                      changed();
                    }}
                  />
                </label>
              )}
            </div>
          )}
          <button
            className={paymentButton}
            disabled={locked}
            onClick={() => void requestPreview()}
          >
            Предпросмотр применения
          </button>
        </>
      )}
      {preview && (
        <div
          className="space-y-2 rounded-md border p-3 text-sm"
          aria-label="Предпросмотр применения"
        >
          <p>Итого по счёту: {formatPaymentRub(preview.totalRub)}</p>
          <p>
            Месячный объём: {preview.monthlyDialogs}; докупка:{" "}
            {preview.topupDialogs}.
          </p>
          <p>
            {upgrade ? "С подтверждения оплаты" : "Начало доступа"}:{" "}
            {preview.accessStartsAt
              ? formatMoscowDateTime(preview.accessStartsAt)
              : "при подтверждении"}
            ; оплачено до {formatMoscowDateTime(preview.paidThroughAt)} МСК.
          </p>
          {upgrade && (
            <p>
              Повышение действует на весь оставшийся срок, включая оплаченные
              продления. Дата расчёта счёта фиксирует цену, а не начало
              повышенного доступа.
            </p>
          )}
          {preview.allocations.map((a) => (
            <p key={a.paymentId} className="break-all">
              Зачёт {a.paymentId}: {formatPaymentRub(a.amountRub)}
            </p>
          ))}
          {preview.newPaymentAllocationRub && (
            <p>
              Из нового перевода:{" "}
              {formatPaymentRub(preview.newPaymentAllocationRub)}
            </p>
          )}
          <p>
            Остаётся незачтённым:{" "}
            {formatPaymentRub(preview.remainingUnallocatedRub)}
          </p>
          <p>Предпросмотр до {formatMoscowDateTime(preview.expiresAt)} МСК.</p>
          {expired && !write.uncertain && (
            <p role="status">Предпросмотр истёк. Получите новый.</p>
          )}
          {!write.uncertain && (
            <button
              className={paymentPrimary}
              disabled={locked || expired}
              onClick={() => setConfirming(true)}
            >
              {upgrade
                ? "Подтвердить оплату и повысить тариф"
                : "Применить покупку"}
            </button>
          )}
        </div>
      )}
      {confirming && (
        <ConfirmDialog
          title={
            upgrade
              ? "Подтвердить оплату и повысить тариф"
              : "Применить покупку"
          }
          description="Будет выполнена показанная сервером операция. Повторное начисление по этому счёту не создаётся."
          confirmLabel="Подтвердить"
          isPending={write.pending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void confirm()}
        />
      )}
      {write.uncertain && (
        <button
          className={paymentPrimary}
          disabled={write.pending}
          onClick={() => void write.retry()}
        >
          Повторить подтверждение
        </button>
      )}
      {(error || write.error) && (
        <p role="alert" className="text-sm text-danger">
          {error ?? write.error}
        </p>
      )}
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
    </section>
  );
}
